import { Button, Icon, Grid, Ref } from "semantic-ui-react";
import React, { Component, createRef } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { observable } from "mobx";
import LoadingWrapper from "~/components/wrapper/LoadingWrapper";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import FormStore from "~/stores/FormStore";
import ModalError from "~/components/element/ModalError";
import ModalUnsavedConfirmation from "~/components/element/ModalUnsavedConfirmation";
import { getValidationErrors } from "~/utils/ReactReformed/ValidateSchema";
import StickyMenu from "~/components/StickyMenu";
import ToastMessageStore from "~/stores/ToastMessageStore";
import ToastMessageContainer from "~/components/ToastMessageContainer";
import CreditCardStore from "~/stores/Credit/CreditCardStore";
import RouteWrapper from "~/components/wrapper/RouteWrapper";
import CustomerStore from "~/stores/CustomerStore";
import CreditCardTable from "~/modules/BillingCentral/components/CreditCardTable";
import ProviderStore from "~/stores/Provider/ProviderStore";
import ContentLoader from "~/components/element/ContentLoader";
import BillingPreferencesForm from "~/modules/BillingCentral/components/BillingPreferencesForm";
import ContactUsLink from "~/modules/BillingCentral/components/ContactUsLink";
import ProductStore from "~/stores/ProductStore";
import ProviderCreditBalanceStore from "~/stores/Provider/ProviderCreditBalanceStore";
import ProductId from "~/constants/ProductId";

@observer
class BillingCentralMainPage extends Component {
  @observable creditCardStore = null;

  @observable formStore = null;

  @observable publishTourProductStore = null;

  @observable providerCreditBalanceStore = null;

  ref = null;

  constructor(props) {
    super(props);

    const { t, actualUserStore } = props;

    this.ref = createRef();
    document.title = t("billingCentral:billingCentral");
    this.creditCardStore = new CreditCardStore();
    this.formStore = new FormStore();

    this.publishTourProductStore = null;
    this.providerCreditBalanceStore = null;

    if (actualUserStore instanceof ProviderStore) {
      this.publishTourProductStore = new ProductStore();
      this.providerCreditBalanceStore = new ProviderCreditBalanceStore();
    }
  }

  componentDidMount() {
    const { showError, actualUserStore } = this.props;
    const { actualUser } = actualUserStore;

    if (!actualUserStore.needReload) {
      this.chainComponentDidMount()
    } else {
      actualUserStore.load().then(() => {
        this.chainComponentDidMount()
      })
    }
  }

  chainComponentDidMount() {
    const { showError, actualUserStore } = this.props;
    const { actualUser } = actualUserStore;

    this.bindForm(actualUserStore);

    this.creditCardStore.pagination.itemsPerPage = 100;
    this.creditCardStore.updateSearchParams({
      currency: actualUser.currency,
    });

    this.creditCardStore.loadIndex().catch(error => showError(error.message));

    if (actualUserStore instanceof ProviderStore) {
      this.providerCreditBalanceStore.load(actualUser.providerId).catch(error => showError(error.message));
      this.publishTourProductStore.productId = ProductId.PUBLISH_TOUR;
      this.publishTourProductStore.load().catch(error => showError(error.message));
    }
  }

  onSubmit() {
    const { t, toastMessageStore, actualUserStore } = this.props;
    const { model } = this.formStore;
    const formActualUser = {
      defaultCreditCardId: model.defaultCreditCardId !== "" ? model.defaultCreditCardId : null,
    };

    if (actualUserStore instanceof ProviderStore) {
      formActualUser.lowCreditBalanceNotificationThreshold =
        model.lowCreditBalanceNotificationThreshold !== "" ? model.lowCreditBalanceNotificationThreshold : null;
      formActualUser.lowCreditBalanceThreshholdAutoPurchaseAmount =
        model.lowCreditBalanceThreshholdAutoPurchaseAmount !== ""
          ? model.lowCreditBalanceThreshholdAutoPurchaseAmount
          : 0;
    }

    return Promise.all([actualUserStore.save(formActualUser)]).then(() => {
      this.bindForm(actualUserStore);
      toastMessageStore.addToastMessage(t("changesSaved"), "positive", t("success"));
    });
  }

  bindForm(actualUserStore) {
    const { actualUser } = actualUserStore;
    const initialModel = {
      defaultCreditCardId: actualUser.defaultCreditCardId !== null ? actualUser.defaultCreditCardId : "",
    };

    if (actualUserStore instanceof ProviderStore) {
      initialModel.lowCreditBalanceNotificationThreshold =
        actualUser.lowCreditBalanceNotificationThreshold !== null
          ? actualUser.lowCreditBalanceNotificationThreshold
          : "";
      initialModel.lowCreditBalanceThreshholdAutoPurchaseAmount =
        actualUser.lowCreditBalanceThreshholdAutoPurchaseAmount !== 0 &&
        actualUser.lowCreditBalanceThreshholdAutoPurchaseAmount !== null
          ? actualUser.lowCreditBalanceThreshholdAutoPurchaseAmount
          : "";
    }

    this.formStore = new FormStore(initialModel);
  }

  render() {
    const { t, toastMessageStore, redirectTo, actualUserStore } = this.props;
    const { isDirty, model } = this.formStore;
    const { isLoading: actualUserLoading, isSaving } = actualUserStore;
    const { isLoading: creditCardLoading, hasCreditCards } = this.creditCardStore;

    const validationRules = {};
    const validation = getValidationErrors(validationRules, model);

    if (actualUserLoading) {
      return <ContentLoader />;
    }

    const or = t("or").toLowerCase();

    return (
      <>
        <ModalUnsavedConfirmation
          when={isDirty}
          onSave={() => this.onSubmit()}
          onDiscard={() => this.bindForm(actualUserStore)}
        />

        <LoadingWrapper loading={isSaving} message={t("saving")} />
        <ModalError nestStore={actualUserStore} />

        <div className="side-page-outer-container">
          <div className="side-page-header">
            <div className="side-page-h1-wrap">
              <h1>{t("billingCentral:billingCentral")}</h1>
            </div>
          </div>

          <Grid stackable verticalAlign="middle" style={{ marginTop: "0.5rem", marginBottom: "0.5rem" }}>
            <Grid.Column width="8" />
            <Grid.Column width="8">
              <div className="side-page-top-buttons">
                <Button primary onClick={() => redirectTo("/billing-central/subscriptions")}>
                  {t("billingCentral:subscriptions")}
                </Button>
                {actualUserStore instanceof ProviderStore && (
                  <>
                    <Button onClick={() => redirectTo("/billing-central/credit-account-history")}>
                      {t("billingCentral:viewAccountHistory")}
                    </Button>
                    <Button
                      secondary
                      loading={creditCardLoading}
                      onClick={() =>
                        redirectTo(`/billing-central/purchase-credits${hasCreditCards ? "" : "-no-card-on-file"}`)
                      }
                    >
                      {t("billingCentral:purchaseCredits")}
                    </Button>
                  </>
                )}
              </div>
              <div className="side-page-contact-us">
                {or} <ContactUsLink isMyUrbanimmersive={false} />.
              </div>
            </Grid.Column>
          </Grid>

          <div className="side-page-header">
            <div className="side-page-h2-wrap">
              <h2>{t("billingCentral:billingPreferences")}</h2>
            </div>
          </div>

          <Ref innerRef={this.ref}>
            <div>
              <StickyMenu
                contextRef={this.ref}
                hideHeaderWhenIsNotSticky
                headerText={t("billingCentral:billingPreferences")}
                button={
                  <div className="side-page-save-btn-wrap">
                    <Button primary icon disabled={!isDirty || !validation.isValid} onClick={() => this.onSubmit()}>
                      <Icon name="save" />
                      {t("saveChanges")}
                    </Button>
                  </div>
                }
              />

              <BillingPreferencesForm
                creditCardStore={this.creditCardStore}
                formStore={this.formStore}
                actualUserStore={actualUserStore}
                publishTourProductStore={this.publishTourProductStore}
              />
            </div>
          </Ref>

          <div className="side-page-header">
            <div className="side-page-h2-wrap">
              <h2>{t("billingCentral:creditCards")}</h2>
            </div>
          </div>

          <CreditCardTable
            actualUserStore={actualUserStore}
            creditCardStore={this.creditCardStore}
            toastMessageStore={toastMessageStore}
          />
        </div>
        <ToastMessageContainer toastMessageStore={toastMessageStore} />
      </>
    );
  }
}

BillingCentralMainPage.propTypes = {
  showError: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  toastMessageStore: PropTypes.instanceOf(ToastMessageStore),
  actualUserStore: PropTypes.oneOfType([PropTypes.instanceOf(ProviderStore), PropTypes.instanceOf(CustomerStore)]),
};

export default withTranslation()(ErrorWrapper(RouteWrapper(BillingCentralMainPage)));
